import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { TextFormattedNumber } from 'components';
import { format } from 'date-fns';
import {
  editDepositApi,
} from 'api';
import NumberFormat from 'react-number-format';
import { convertUTCDateToLocalDate } from 'helpers/date';
import {
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import { depositStatus } from '../helpers';


const EditDepositDetails = () => {
  const dispatch = useDispatch();
  const { order_id } = useParams<{ entity_id: string, order_id: string }>();

  const { editableDeposit } = useSelector((state: IReduxState) => state.deposits);

  const {
    deposit,
    first_interval_price,
    interval_price,
    billing_interval,
    length,
    run_count,
    last_run,
    next_run,
    status,
    created_at,
    customer_id,
    firstname,
    lastname,
    email
  } = editableDeposit;

  const handleSaveDeposit = (values) => {
    const deposit = {
      ...editableDeposit,
      status: values.status
    };

    editDepositApi({dispatch, deposit});
  };

  const lastRunDate = convertUTCDateToLocalDate(last_run || new Date());
  const startDate = convertUTCDateToLocalDate(created_at || new Date());
  const nextRunDate = convertUTCDateToLocalDate(next_run || new Date());

  const formattedLastRunDate = format(lastRunDate, 'MM/dd/yyyy hh:mm:ss aa');
  const formattedStartDate = format(startDate, 'MM/dd/yyyy hh:mm:ss aa');
  const formattedNextRunDate = format(nextRunDate, 'MM/dd/yyyy hh:mm:ss aa');

  return (
    <>
      <Formik
        initialValues={{
          status: status || ''
        }}
        validationSchema={yup.object({
          status: yup
            .string()
            .required('Field is required')
        })}
        enableReinitialize
        onSubmit={(values) => handleSaveDeposit(values)}
      >
        {({
          values,
          errors,
          touched,
          isValid,
          dirty,
          handleBlur,
          handleChange,
          handleSubmit
        }) => (
          <form noValidate autoComplete="off" className="py-4 px-2" onSubmit={handleSubmit}>
            <Grid container spacing={2} className="flex">
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Status
                </Typography>
                <TextField
                  required
                  select
                  variant="outlined"
                  label="Select status"
                  name="status"
                  className="w-full capitalize"
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.status && Boolean(errors?.status)}
                  helperText={touched?.status && errors?.status}
                >
                  {Object.keys(depositStatus).map((status) => (
                    <MenuItem
                      key={`status-${status}`}
                      value={status}
                    >
                      <span className="capitalize">{status.replace('_', ' ')}</span>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Started
                </Typography>
                <Typography className="ml-2">
                  {formattedStartDate}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography className="mb-2" variant="h6">
                  Last Run
                </Typography>
                <Typography className="ml-2">
                  {last_run ? formattedLastRunDate : formattedStartDate}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography className="mb-2" variant="h6">
                  Next Run
                </Typography>
                <Typography className="ml-2">
                  {next_run ? formattedNextRunDate : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Order ID
                </Typography>
                <Typography className="ml-2">
                  <Link className="text-primary underline" to={{pathname: '/orders/list', state: {orderId: order_id}}}>
                    {order_id}
                  </Link>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Deposit
                </Typography>
                <Typography className="ml-2">
                  <TextFormattedNumber
                    value={deposit}
                    prefix="$"
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Number of times billed
                </Typography>
                <Typography className="ml-2">
                  {run_count}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  First Interval Price
                </Typography>
                <Typography className="ml-2">
                  <NumberFormat
                    value={first_interval_price}
                    displayType="text"
                    thousandSeparator={','}
                    decimalSeparator={'.'}
                    prefix={'$'}
                    fixedDecimalScale
                    decimalScale={2}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Interval Price
                </Typography>
                <Typography className="ml-2">
                  <NumberFormat
                    value={interval_price}
                    displayType="text"
                    thousandSeparator={','}
                    decimalSeparator={'.'}
                    prefix={'$'}
                    fixedDecimalScale
                    decimalScale={2}
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Length
                </Typography>
                <Typography className="ml-2">
                  {length}
                </Typography>
                <Typography className="text-13 text-light-gray">
                  Number of cycles the deposit should run.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Interval (days)
                </Typography>
                <Typography className="ml-2">
                  {billing_interval}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className="mb-2" variant="h6">
                  Customer
                </Typography>
                <Typography className="ml-2">
                  <Link
                    className="underline text-primary mr-2"
                    to={`/customers/${customer_id}/details`}
                  >
                    {`${firstname} ${lastname}`}
                  </Link>
                  {`(${email})`}
                </Typography>
              </Grid>
            </Grid>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className="mt-4"
              disabled={!(isValid && dirty)}
            >
              Save Deposit
            </Button>
          </form>
        )}
      </Formik>
    </>
  );
};

export default EditDepositDetails;
